import React, { useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';

interface Tax {
  dateOfBirth: string;
  statusOfResidency: string;
  incomeType: string;
  tfnExemption: string;
  taxFileNumber: string;
  claimTaxFreeThreshold: boolean;
  hasStudyAndTrainingLoan: boolean;
  isHorticulturistOrShearer: boolean;
  hasChildSupportObligations: boolean;
}

interface EmployeeDetailsForm {
  personal: Record<string, any>;
  bankAndSuper: Record<string, any>;
  employment: Record<string, any>;
  payRates: Record<string, any>;
  tax: Tax;
  openingBalances: Record<string, any>;
  template: Record<string, any>;
}

const TaxTab: React.FC = () => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues
  } = useFormContext<EmployeeDetailsForm>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const taxFileNumber = watch('tax.taxFileNumber');
  const [showTaxFileNumber, setShowTaxFileNumber] = useState(!!getValues('tax.taxFileNumber'));

  useEffect(() => {
    // Set initial visibility based on patched taxFileNumber value
    setShowTaxFileNumber(!!getValues('tax.taxFileNumber'));
  }, []); // Empty dependency array to run only on mount

  const handleToggleTaxFileNumber = () => {
    if (showTaxFileNumber) {
      // Clear taxFileNumber when hiding the field
      setValue('tax.taxFileNumber', '', { shouldValidate: true });
    }
    setShowTaxFileNumber(!showTaxFileNumber);
  };

  const residencyOptions = ['Australian resident', 'Foreign resident', 'Working holiday'];

  const incomeTypeOptions = [
    'SAW (Salary and Wages)',
    'CHP (Closely Held Payees)',
    'SWP (Seasonal Worker Programme)'
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Tax Details
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.dateOfBirth"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!!errors.tax?.dateOfBirth}
                helperText={errors.tax?.dateOfBirth?.message || ''}
                fullWidth
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.statusOfResidency"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.tax?.statusOfResidency}>
                <InputLabel>Status of Residency</InputLabel>
                <Select
                  {...field}
                  label="Status of Residency"
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {residencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tax?.statusOfResidency && (
                  <Typography variant="caption" color="error">
                    {errors.tax.statusOfResidency.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.incomeType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.tax?.incomeType}>
                <InputLabel>Income Type</InputLabel>
                <Select
                  {...field}
                  label="Income Type"
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {incomeTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tax?.incomeType && (
                  <Typography variant="caption" color="error">
                    {errors.tax.incomeType.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.tfnExemption"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="TFN Exemption"
                error={!!errors.tax?.tfnExemption}
                helperText={errors.tax?.tfnExemption?.message || ''}
                fullWidth
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="text"
            onClick={handleToggleTaxFileNumber}
            sx={{ textTransform: 'none' }}
            size="small"
          >
            {showTaxFileNumber ? 'No tax file number' : 'Enter tax file number'}
          </Button>
        </Grid>

        {showTaxFileNumber && (
          <Grid item xs={12} sm={12}>
            <Controller
              name="tax.taxFileNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Tax File Number"
                  error={!!errors.tax?.taxFileNumber}
                  helperText={errors.tax?.taxFileNumber?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.claimTaxFreeThreshold"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Claim Tax Free Threshold"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.hasStudyAndTrainingLoan"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Has Study and Training Loan"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.isHorticulturistOrShearer"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Is Horticulturist or Shearer"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="tax.hasChildSupportObligations"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Has Child Support Obligations"
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaxTab;
